import React from 'react';
import { Card, CardContent, Typography, Box, Stack, Divider, Link } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import NumbersIcon from '@mui/icons-material/Numbers';
import LinkIcon from '@mui/icons-material/Link';
import LaunchIcon from '@mui/icons-material/Launch';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { getFiscalYearEndLabel } from './fiscalYearEnd';

interface CompanyDetailsSectionProps {
  company: Record<string, unknown> | null;
}

const formatIsoDate = (value: unknown): string => {
  if (typeof value !== 'string' || !value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toISOString().slice(0, 10);
};

const CompanyDetailsSection: React.FC<CompanyDetailsSectionProps> = ({ company }) => {
  if (!company) return null;

  return (
    <Card sx={{ borderRadius: 2 }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon color="primary" />
          Company Details
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Stack spacing={2}>
          {company.registrationNumber ? (
            <Box display="flex" alignItems="center" gap={1} minWidth={0}>
              <NumbersIcon fontSize="small" color="action" />
              <Box minWidth={0}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Registration No.
                </Typography>
                <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{String(company.registrationNumber)}</Typography>
              </Box>
            </Box>
          ) : null}
          <Box display="flex" alignItems="center" gap={1} minWidth={0}>
            <CalendarTodayIcon fontSize="small" color="action" />
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Fiscal Year End
              </Typography>
              <Typography variant="body2">
                {getFiscalYearEndLabel(company.fiscalYearEndMonth)}
              </Typography>
            </Box>
          </Box>
          {company.corporateFilingUrl ? (
            <Box display="flex" alignItems="flex-start" gap={1} minWidth={0}>
              <LinkIcon fontSize="small" color="action" />
              <Box minWidth={0}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Corporate Filing
                </Typography>
                <Link
                  href={String(company.corporateFilingUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 32, overflowWrap: 'anywhere' }}
                >
                  <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{String(company.corporateFilingUrl)}</Typography>
                  <LaunchIcon fontSize="inherit" />
                </Link>
              </Box>
            </Box>
          ) : null}
          {company.incorporationDate ? (
            <Box display="flex" alignItems="center" gap={1} minWidth={0}>
              <CalendarTodayIcon fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Incorporation Date
                </Typography>
                <Typography variant="body2">
                  {formatIsoDate(company.incorporationDate)}
                </Typography>
              </Box>
            </Box>
          ) : null}
          {company.address ? (
            <Box display="flex" alignItems="flex-start" gap={1} minWidth={0}>
              <LocationOnIcon fontSize="small" color="action" sx={{ mt: 0.5 }} />
              <Box minWidth={0}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Address
                </Typography>
                <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{String(company.address)}</Typography>
              </Box>
            </Box>
          ) : null}
          {company.authorisedShareCapitalRaw ? (
            <Box display="flex" alignItems="flex-start" gap={1} minWidth={0}>
              <AccountBalanceIcon fontSize="small" color="action" sx={{ mt: 0.5 }} />
              <Box minWidth={0}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Authorized Capital
                </Typography>
                <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{String(company.authorisedShareCapitalRaw)}</Typography>
              </Box>
            </Box>
          ) : null}
          {company.oldName ? (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Former Name
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                {String(company.oldName)}
              </Typography>
            </Box>
          ) : null}
          {company.comments || company.notes ? (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Notes
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                {String(company.comments || company.notes)}
              </Typography>
            </Box>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default CompanyDetailsSection;
